import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Box } from "@mui/material";
import * as d3 from "d3";

const GraphWrapper = styled(Box)(`
  width: 100%;
  height: 600px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  & h4 {
    color: #000;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    margin-top: 12px;
  }
`);

const colorArray = [
  { main: "#6872f5" },
  { main: "#349aee" },
  { main: "#23b9d1" },
  { main: "#c253dd" },
  { main: "#f64784" },
];

const FONT_FAMILY = "Arial, Helvetica, sans-serif";
const midResolution = 1250;
const lowResolution = 1050;

const config = {
  innerRadius: 72,
  outerRadius: 480,
  startAngle: 0,
  endAngle: 360,
  connectorLine1MaxLength: 40, 
  connectorLine2MaxLength: 40,
  contentDistanceFromCircle: -140, 
  descriptionMinWidth: 280, 
  descriptionBoxPadding: 10, 
  descriptionGapFromLabel: 8,          
  descriptionLineHeight: 16,          
  descriptionFontSize: '14px',
  labelLineHeight: 18,                
  edgeMargin: 10, 
  verticalGap: 80, 
  lineRatio: 30, 
  topOffset: -80, 
  bottomTextTopOffset: 50,
  bottomTextWidth: 80,
};

let measureCanvas;
const measureTextWidth = (text, font) => {
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const ctx = measureCanvas.getContext("2d");
  ctx.font = font;
  return ctx.measureText(text).width;
};

const wrapTextByWidth = (text = "", maxWidth, font) => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (measureTextWidth(candidate, font) > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });

  if (current) lines.push(current);
  return lines;
};

const D3BarAndPieChart = ({ rawDataNew = [], rawData = [], heading, parentWidth, isPPTModalActive }) => {
  const svgRef = useRef(null);
  const [height, setHeight] = useState(600);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : lowResolution);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dataSource = rawDataNew.length > 0 ? rawDataNew : rawData;
  
  const activeData = (
    dataSource.length > 0 ? dataSource : []
  ).map((d, i) => ({
    ...d,
    main: d.color || d.main || colorArray[i % colorArray.length].main,
  }));

  useEffect(() => {
    const width = parentWidth || windowWidth;
    const height = parentWidth > lowResolution ? 600 : 400; 
    setHeight(height);

    let {
      innerRadius,
      outerRadius,
      connectorLine1MaxLength, 
      connectorLine2MaxLength,
      contentDistanceFromCircle, 
      descriptionMinWidth,
      descriptionBoxPadding,
      descriptionGapFromLabel,
      descriptionLineHeight,
      descriptionFontSize,
      labelLineHeight,
      edgeMargin,
      verticalGap,
      lineRatio,
      topOffset,
      bottomTextTopOffset,
      bottomTextWidth
    } = config;

    if(parentWidth < midResolution) {
      contentDistanceFromCircle = -200;
      descriptionFontSize = '12px';
      descriptionLineHeight = 13;
      descriptionMinWidth = 260;
      labelLineHeight = 18;
      verticalGap = 150;
    }
    if(parentWidth < lowResolution) {
      // innerRadius = 45;
      outerRadius = 280;
      connectorLine1MaxLength = 20;
      contentDistanceFromCircle = -60;
      descriptionFontSize = '12px';
      descriptionLineHeight = 13;
      descriptionMinWidth = 180;
      labelLineHeight = 18;
      edgeMargin = 5;
      verticalGap = 120;
      topOffset = -50;
      bottomTextTopOffset = -320;
      bottomTextWidth = 80;
    }
  
    if(isPPTModalActive) {
      contentDistanceFromCircle = -110;
      edgeMargin = 50;
    }

    const labelOffset = outerRadius + contentDistanceFromCircle;
    const safeTop = -(height / 2) + 10;
    const safeBottom = height / 2 - edgeMargin;
    const sideSafetyPadding = 70; 
    
    const uniformBoxWidth = Math.max(
      descriptionMinWidth, 
      (width / 2) - (labelOffset - sideSafetyPadding) - edgeMargin
    )
    
    const currentTextWidth = Math.max(50, uniformBoxWidth - (descriptionBoxPadding * 2));
    const descriptionFont = `400 14px ${FONT_FAMILY}`;
    const labelFont = `600 14px ${FONT_FAMILY}`;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2 + topOffset})`);

    // Linear scale for Radial Height calculation
    const y = d3
      .scaleLinear()
      .range([innerRadius, outerRadius])
      .domain([0, 100]);

    // Generate dynamic pie layout angles where slice width matches item values
    const pieLayout = d3
      .pie()
      .value((d) => d.value)
      .sort(null);

    const pieData = pieLayout(activeData);
    const gap = 0.02; // Padding angle between visual sections in radians

    // Draw variable width & variable height sections
    svg.selectAll(".main")
      .data(pieData)
      .enter()
      .append("path")
      .attr("class", "main")
      .attr("fill", d => d.data.main)
      .attr("d", d => {
        return d3.arc()
          .innerRadius(innerRadius)
          .outerRadius(y(d.data.value))
          .startAngle(d.startAngle + gap / 2)
          .endAngle(d.endAngle - gap / 2)();
      });

    // Draw labels correctly centered inside dynamic widths/heights
    svg.selectAll(".pct")
       .data(pieData)
       .enter()
       .append("text")
       .attr("class", "pct")
       .attr("transform", (d) => {
          const angle = (d.startAngle + d.endAngle) / 2;
          const radius = innerRadius + (y(d.data.value) - innerRadius) / 2;
          const xPos = Math.cos(angle - Math.PI / 2) * radius;
          const yPos = Math.sin(angle - Math.PI / 2) * radius;
          return `translate(${xPos}, ${yPos})`;
      })
      .attr("text-anchor", "middle")
      .attr("alignment-baseline", "middle")
      .attr("fill", "#fff")
      .style("font-family", FONT_FAMILY)
      .style("font-size", "12px")
      .style("font-weight", "600")
      .text((d) => `${d.data.value}%`);
    
    // if(parentWidth > lowResolution) 
      svg.append("circle").attr("r", innerRadius - 18).attr("fill", "#fff").attr("stroke", "#000").attr("stroke-width", 1);
    svg.append("text").attr("text-anchor", "middle").attr("dominant-baseline", "middle").style("font-family", FONT_FAMILY).style("font-size", "16px").style("font-weight", "600").style("fill", "#000").text(heading);

    const claimedBounds = { left: [], right: [] };
    const rangesOverlap = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && bStart < aEnd;

    const findNonCollidingY = (side, desiredY, blockHeight, padding = verticalGap) => {
      let candidateStart = desiredY - blockHeight / 2;
      let candidateEnd = desiredY + blockHeight / 2;
      const bounds = claimedBounds[side];

      let moved = true;
      while (moved) {
        moved = false;
        for (const [start, end] of bounds) {
          if (rangesOverlap(candidateStart - padding, candidateEnd + padding, start, end)) {
            const shift = end + padding - candidateStart;
            candidateStart += shift;
            candidateEnd += shift;
            moved = true;
          }
        }
      }
      bounds.push([candidateStart, candidateEnd]);
      return candidateStart + blockHeight / 2;
    };

    const totalCount = pieData.length;
    const isOdd = totalCount % 2 !== 0;
    const centerIndex = isOdd ? Math.floor(totalCount / 2) : -1;

    const preparedBlocks = pieData.map((d, index) => {
      const angle = (d.startAngle + d.endAngle) / 2;
      const barEnd = y(d.data.value);
      const dotX = Math.cos(angle - Math.PI / 2) * barEnd;
      const dotY = Math.sin(angle - Math.PI / 2) * barEnd;

      let side = "right";
      if (isOdd && index === centerIndex) {
        side = "bottom";
      } else {
        side = dotX < 0 ? "left" : "right";
      }

      const isRight = side === "right";
      const isBottom = side === "bottom";

      const bottomTextWidth = currentTextWidth + config.bottomTextWidth;
      const textWidth = isBottom ? bottomTextWidth : currentTextWidth;
      
      
      // const labelLines = wrapTextByWidth(`${d.data.value}% ${d.data.name}`, currentTextWidth, labelFont);
      const labelLines = wrapTextByWidth( `${d.data.value}% ${d.data.name}`, textWidth, labelFont );
      const numLabelLines = Math.max(labelLines.length, 1);
      const labelBlockHeight = numLabelLines * labelLineHeight;
      
      // const descLines = wrapTextByWidth(d.data.description, currentTextWidth, descriptionFont);
      const descLines = wrapTextByWidth( d.data.description, textWidth, descriptionFont );
      const numDescLines = descLines.length;
      const descBlockHeight = numDescLines ? (numDescLines * descriptionLineHeight) : 0;
      const totalBlockHeight = labelBlockHeight + (numDescLines ? descriptionGapFromLabel + descBlockHeight : 0);

      return { 
        d: d.data, dotX, dotY, isRight, isBottom, side, barEnd, angle, 
        labelLines, descLines, numDescLines, labelBlockHeight, 
        descBlockHeight, totalBlockHeight 
      };
    });

    const layoutBlocks = preparedBlocks.filter(b => !b.isBottom).sort((a, b) => a.dotY - b.dotY);
    const bottomBlock = preparedBlocks.find(b => b.isBottom);
    const firstRightBlock = layoutBlocks.find(b => b.side === "right");

    const renderBlock = (blockItem) => {
      const { 
        d, dotX, dotY, isRight, isBottom, side, barEnd, angle, 
        labelLines, descLines, numDescLines, labelBlockHeight, 
        descBlockHeight, totalBlockHeight
      } = blockItem;

      let textX, labelTopY, pathD;

      const line1StartX = Math.cos(angle - Math.PI / 2) * (barEnd + 4);
      const line1StartY = Math.sin(angle - Math.PI / 2) * (barEnd + 4);

      if (isBottom) {
        const bottomBoxWidth = uniformBoxWidth + config.bottomTextWidth; // increase as needed
        textX = -bottomBoxWidth / 2;
        labelTopY = barEnd + lineRatio + 50;
        
        if(parentWidth < lowResolution) {
          textX = -bottomBoxWidth / 2 + 20;
          labelTopY = barEnd + lineRatio + 0;
        }


        pathD = `M ${line1StartX} ${line1StartY}
                L ${textX - descriptionBoxPadding + 15} ${labelTopY - 22}
                L ${textX - descriptionBoxPadding + 15} ${labelTopY - 12}`;
      } else {
        const connectorEndX = isRight ? (labelOffset - sideSafetyPadding) : -(labelOffset - sideSafetyPadding);
        const boxLeftX = isRight ? connectorEndX : (connectorEndX - uniformBoxWidth);
        textX = boxLeftX + descriptionBoxPadding;
        const attachX = isRight ? connectorEndX - 4 : connectorEndX + 4;

        let desiredCenterY = dotY - 35; 
        if (blockItem === firstRightBlock) {
          desiredCenterY -= 50;
        }

        let blockCenterY = findNonCollidingY(side, desiredCenterY, totalBlockHeight);
        let blockTop = blockCenterY - totalBlockHeight / 2;
        let blockBottom = blockCenterY + totalBlockHeight / 2;

        const labelCenterYPreCap = blockTop + labelBlockHeight / 2;
        const rawShift = labelCenterYPreCap - dotY;
        if (Math.abs(rawShift) > connectorLine2MaxLength) {
          const correction = rawShift - Math.sign(rawShift) * connectorLine2MaxLength;
          blockCenterY -= correction;
          blockTop -= correction;
          blockBottom -= correction;
        }

        if (blockTop < safeTop) {
          const shift = safeTop - blockTop;
          blockCenterY += shift;
          blockTop += shift;
          blockBottom += shift;
        }
        if (blockBottom > safeBottom) {
          const shift = blockBottom - safeBottom;
          blockCenterY -= shift;
          blockTop -= shift;
          blockBottom -= shift;
        }

        labelTopY = blockTop;

        const safeRadialExtension = barEnd + 15; 
        const breakPointX = Math.cos(angle - Math.PI / 2) * safeRadialExtension;
        const breakPointY = Math.sin(angle - Math.PI / 2) * safeRadialExtension;

        const rawTargetX = attachX;
        const dx = rawTargetX - breakPointX;
        const currentLength = Math.abs(dx);

        
        let line1EndX = rawTargetX;
        if (currentLength > connectorLine1MaxLength && currentLength > 0) {
          const scale = connectorLine1MaxLength / currentLength;
          line1EndX = breakPointX + dx * scale;
        }

        pathD = `M ${line1StartX} ${line1StartY}
                 L ${line1EndX} ${labelTopY + 5}
                 L ${isRight ? textX - descriptionBoxPadding : textX + uniformBoxWidth - descriptionBoxPadding - uniformBoxWidth} ${labelTopY + 5}`;
        
 
      }

      const descTextTop = labelTopY + labelBlockHeight + descriptionGapFromLabel;

      svg.append("circle").attr("cx", dotX).attr("cy", dotY).attr("r", 6).attr("fill", d.main).attr("stroke", "#fff").attr("stroke-width", 1);
      
      svg.append("path")
        .attr("d", pathD)
        .attr("fill", "none")
        .attr("stroke", "#ADADAC")
        .attr("stroke-width", 1);
      
      // Title Background
      const computedTextWidth = d3.max(labelLines, line => measureTextWidth(line, labelFont)) || 0;
      const titleBgWidth = computedTextWidth + (descriptionBoxPadding * 2);

      svg.append("rect")
        .attr("x", textX - descriptionBoxPadding)
        .attr("y", labelTopY - descriptionBoxPadding)
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("fill", '#fff')
        .attr("class", 'titleBg')
        .attr("height", labelBlockHeight + descriptionBoxPadding)
        .attr("width", titleBgWidth)
        .style("opacity", 1);

      const link = svg.append("a")
        .attr("xlink:href", d.url || "#")
        .style("cursor", "pointer");

      const labelText = link
        .append("text")
        .attr("x", textX)
        .attr("y", labelTopY)
        .attr("text-anchor", "start")
        .attr("dominant-baseline", "hanging")
        .attr("id", 'txt_'+(d.name.replaceAll(' ','')))
        .style("font-family", FONT_FAMILY)
        .style("font-size", descriptionFontSize)
        .style("font-weight", "600");

      labelLines.forEach((line, i) => {
        const tspan = labelText
          .append("tspan")
          .attr("x", textX)
          .attr("dy", i === 0 ? 0 : labelLineHeight);

        const isFirstLine = i === 0;
        if (isFirstLine) {
            tspan.append("tspan").attr("fill", d.main).text(`${d.value}% `);
            tspan.append("tspan").attr("fill", "#000").text(line.replace(`${d.value}%`, "").trim());
        } else {
            tspan.attr("fill", "#000").text(line);
        }
      });

      if (numDescLines) {
        const descText = svg
          .append("text")
          .attr("x", textX)
          .attr("y", descTextTop)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "hanging")
          .style("font-family", FONT_FAMILY)
          .style("font-size", descriptionFontSize)
          .style("font-weight", "400")
          .style("fill", "#000");

        descLines.forEach((line, i) => {
          descText
            .append("tspan")
            .attr("x", textX)
            .attr("dy", i === 0 ? 0 : descriptionLineHeight)
            .text(line);
        });
      }
    };

    layoutBlocks.forEach(renderBlock);
    if (bottomBlock) renderBlock(bottomBlock);

  }, [activeData, windowWidth, parentWidth, heading]);

  return (
    <GraphWrapper style={{height: height+'px'}}>
      <svg ref={svgRef}></svg>
    </GraphWrapper>
  );
};

export default D3BarAndPieChart;